/** Highlight colors matching RichTextEditor selection popover. */
export const GUIDE_HIGHLIGHT_COLORS = [
  { value: '#fff3a3', token: 'yellow', label: 'Yellow' },
  { value: '#b7f7c5', token: 'green', label: 'Green' },
  { value: '#ffc9de', token: 'pink', label: 'Pink' },
  { value: '#a8d8ff', token: 'blue', label: 'Blue' }
];

const HIGHLIGHT_TOKEN_BY_VALUE = {
  '#fff3a3': 'yellow',
  '#b7f7c5': 'green',
  '#ffc9de': 'pink',
  '#a8d8ff': 'blue',
  'rgb(255,243,163)': 'yellow',
  'rgb(183,247,197)': 'green',
  'rgb(255,201,222)': 'pink',
  'rgb(168,216,255)': 'blue',
  'rgba(255,243,163,1)': 'yellow',
  'rgba(183,247,197,1)': 'green',
  'rgba(255,201,222,1)': 'pink',
  'rgba(168,216,255,1)': 'blue',
  'rgba(255,193,7,0.22)': 'yellow',
  'rgba(40,167,69,0.22)': 'green',
  'rgba(214,51,132,0.22)': 'pink',
  'rgba(33,150,243,0.22)': 'blue'
};

export function highlightColorToken(color) {
  const compact = String(color || '').trim().toLowerCase().replace(/\s+/g, '');
  return compact ? (HIGHLIGHT_TOKEN_BY_VALUE[compact] || null) : null;
}

const CONTEXT_LEN = 32;

function isSkippableAnnotationNode(node) {
  return node?.nodeType === 1 && (
    node.classList?.contains('guides-anno-gutter')
    || node.classList?.contains('guides-anno-gutter-layer')
  );
}

/** Collect text nodes under root, skipping gutter UI. */
export function getContentTextNodes(root) {
  const nodes = [];
  if (!root) return nodes;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let p = node.parentElement;
      while (p && p !== root) {
        if (isSkippableAnnotationNode(p) || p.classList?.contains('guides-anno-gutter')) {
          return NodeFilter.FILTER_REJECT;
        }
        p = p.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  let n = walker.nextNode();
  while (n) {
    nodes.push(n);
    n = walker.nextNode();
  }
  return nodes;
}

/** Plain text of content root (excluding gutter nodes). */
export function getContentPlainText(root) {
  return getContentTextNodes(root).map((n) => n.nodeValue || '').join('');
}

/**
 * Map a DOM Range (inside root) to absolute char offsets + quote/prefix/suffix.
 */
export function rangeToAnnotationAnchor(root, range) {
  if (!root || !range || range.collapsed) return null;
  if (!root.contains(range.commonAncestorContainer)) return null;

  let startOffset;
  let endOffset;
  try {
    const pre = range.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    // Exclude gutter layer text if any leaked into measurement — gutters live outside content root
    startOffset = pre.toString().length;
    endOffset = startOffset + range.toString().length;
  } catch {
    return null;
  }

  const full = getContentPlainText(root);
  // Prefer measuring against plain text nodes so gutters/UI outside don't skew offsets
  if (endOffset > full.length) {
    endOffset = Math.min(endOffset, full.length);
  }
  if (startOffset < 0 || endOffset <= startOffset) return null;

  let quote = full.slice(startOffset, endOffset);
  const selected = String(range.toString() || '');
  if (!quote.trim() && selected.trim()) {
    // Fallback if toString() length differed from text-node walk
    const idx = full.indexOf(selected);
    if (idx < 0) return null;
    startOffset = idx;
    endOffset = idx + selected.length;
    quote = selected;
  }
  if (!quote.trim()) return null;

  return {
    quote,
    startOffset,
    endOffset,
    prefix: full.slice(Math.max(0, startOffset - CONTEXT_LEN), startOffset),
    suffix: full.slice(endOffset, Math.min(full.length, endOffset + CONTEXT_LEN))
  };
}

function resolveOffsets(root, annotation) {
  const full = getContentPlainText(root);
  const { quote, startOffset, endOffset, prefix, suffix } = annotation;
  if (
    Number.isFinite(startOffset)
    && Number.isFinite(endOffset)
    && endOffset > startOffset
    && full.slice(startOffset, endOffset) === quote
  ) {
    return { start: startOffset, end: endOffset };
  }

  // Prefer prefix+quote+suffix search
  if (quote) {
    const needle = `${prefix || ''}${quote}${suffix || ''}`;
    if (prefix || suffix) {
      const idx = full.indexOf(needle);
      if (idx >= 0) {
        const start = idx + (prefix || '').length;
        return { start, end: start + quote.length };
      }
    }
    const idx = full.indexOf(quote);
    if (idx >= 0) return { start: idx, end: idx + quote.length };
  }
  return null;
}

function pointAtOffset(nodes, offset) {
  let remaining = offset;
  for (const node of nodes) {
    const len = (node.nodeValue || '').length;
    if (remaining <= len) {
      return { node, offset: Math.max(0, remaining) };
    }
    remaining -= len;
  }
  const last = nodes[nodes.length - 1];
  if (!last) return null;
  return { node: last, offset: (last.nodeValue || '').length };
}

export function annotationToRange(root, annotation) {
  if (!root || !annotation) return null;
  const resolved = resolveOffsets(root, annotation);
  if (!resolved) return null;
  const nodes = getContentTextNodes(root);
  if (!nodes.length) return null;
  const start = pointAtOffset(nodes, resolved.start);
  const end = pointAtOffset(nodes, resolved.end);
  if (!start || !end) return null;
  try {
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  } catch {
    return null;
  }
}

function buildMarkElement(annotation) {
  const mark = document.createElement('mark');
  mark.className = 'guides-anno';
  mark.dataset.annoId = String(annotation.id);
  if (annotation.style === 'underline') mark.classList.add('guides-anno-underline');
  if (annotation.style === 'strike') mark.classList.add('guides-anno-strike');
  if (annotation.style === 'highlight' && annotation.highlightColor) {
    mark.classList.add('guides-anno-highlight');
    const token = highlightColorToken(annotation.highlightColor);
    if (token) {
      mark.classList.add(`guides-anno-hl-${token}`);
      mark.style.backgroundColor = `var(--rte-hl-${token})`;
    } else {
      mark.style.backgroundColor = annotation.highlightColor;
    }
  }
  if (annotation.commentBody) {
    mark.classList.add('guides-anno-has-comment');
  }
  if (!annotation.style) {
    mark.classList.add('guides-anno-comment-only');
  }
  return mark;
}

function rangeOffsetsInTextNode(range, node) {
  const len = (node.nodeValue || '').length;
  if (!len) return null;

  try {
    // comparePoint: -1 before range, 0 inside, 1 after (end is exclusive)
    if (range.comparePoint(node, 0) > 0) return null;
    if (range.comparePoint(node, len) < 0) return null;
  } catch {
    return null;
  }

  let start = 0;
  let end = len;
  if (range.startContainer === node) start = range.startOffset;
  if (range.endContainer === node) end = range.endOffset;
  start = Math.max(0, Math.min(start, len));
  end = Math.max(start, Math.min(end, len));
  if (end <= start) return null;
  return { start, end };
}

function wrapTextPortion(node, start, end, annotation) {
  const text = node.nodeValue || '';
  if (end <= start || start < 0 || end > text.length) return null;
  if (!text.slice(start, end)) return null;

  let working = node;
  if (end < text.length) working.splitText(end);
  const target = start > 0 ? working.splitText(start) : working;
  if (!target.parentNode) return null;

  const mark = buildMarkElement(annotation);
  target.parentNode.insertBefore(mark, target);
  mark.appendChild(target);
  return mark;
}

function getTextNodesIntersectingRange(range) {
  const ancestor = range.commonAncestorContainer;
  const walkRoot = ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentNode : ancestor;
  if (!walkRoot) return [];

  const nodes = [];
  const walker = document.createTreeWalker(walkRoot, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let p = node.parentElement;
      while (p) {
        if (isSkippableAnnotationNode(p)) return NodeFilter.FILTER_REJECT;
        p = p.parentElement;
      }
      try {
        if (!range.intersectsNode(node)) return NodeFilter.FILTER_REJECT;
      } catch {
        return NodeFilter.FILTER_REJECT;
      }
      if (!(node.nodeValue || '').length) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  let n = walker.nextNode();
  while (n) {
    nodes.push(n);
    n = walker.nextNode();
  }
  return nodes;
}

/**
 * Wrap selected text in annotation marks without splitting block elements.
 * Cross-paragraph selections get one mark per text run inside each paragraph.
 */
export function wrapRangeWithAnnotation(range, annotation) {
  if (!range || range.collapsed || !annotation?.id) return [];

  const nodes = getTextNodesIntersectingRange(range);
  if (range.startContainer?.nodeType === Node.TEXT_NODE && !nodes.includes(range.startContainer)) {
    nodes.unshift(range.startContainer);
  }
  if (
    range.endContainer?.nodeType === Node.TEXT_NODE
    && range.endContainer !== range.startContainer
    && !nodes.includes(range.endContainer)
  ) {
    nodes.push(range.endContainer);
  }
  if (!nodes.length) return [];

  // Snapshot clips before wrapping — splitText mutates the live Range.
  const clips = [];
  const seen = new Set();
  nodes.forEach((node) => {
    if (seen.has(node)) return;
    seen.add(node);
    const clip = rangeOffsetsInTextNode(range, node);
    if (clip) clips.push({ node, ...clip });
  });

  const marks = [];
  for (let i = clips.length - 1; i >= 0; i -= 1) {
    const { node, start, end } = clips[i];
    const mark = wrapTextPortion(node, start, end, annotation);
    if (mark) marks.push(mark);
  }
  return marks;
}

/** Remove all annotation wraps/gutters from root; returns cleaned HTML-equivalent live DOM. */
export function clearAnnotationMarks(root) {
  if (!root) return;
  const article = root.closest?.('.guides-read-article') || root.parentElement;
  article?.querySelectorAll?.('.guides-anno-gutter-layer').forEach((el) => el.remove());
  root.querySelectorAll('.guides-anno-gutter').forEach((el) => el.remove());
  const marks = Array.from(root.querySelectorAll('mark.guides-anno'));
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
  });
  root.normalize();
}

/**
 * Apply all annotations onto the live content root (marks + optional gutters).
 * Gutters are placed in `.guides-anno-gutter-layer` aligned to the right of the article.
 */
export function applyAnnotationsToRoot(root, annotations, { onGutterClick } = {}) {
  const positions = [];
  if (!root || !Array.isArray(annotations)) return positions;

  clearAnnotationMarks(root);

  const article = root.closest('.guides-read-article') || root.parentElement;
  let layer = article?.querySelector?.('.guides-anno-gutter-layer');
  if (!layer && article) {
    layer = document.createElement('div');
    layer.className = 'guides-anno-gutter-layer';
    layer.setAttribute('aria-hidden', 'false');
    article.style.position = article.style.position || 'relative';
    article.appendChild(layer);
  } else if (layer) {
    layer.innerHTML = '';
  }

  // Apply from end to start so earlier offsets stay valid
  const sorted = [...annotations].sort((a, b) => b.startOffset - a.startOffset || b.id - a.id);
  sorted.forEach((anno) => {
    const range = annotationToRange(root, anno);
    if (!range) return;
    wrapRangeWithAnnotation(range, anno);
  });

  annotations.forEach((anno) => {
    const mark = root.querySelector(`mark.guides-anno[data-anno-id="${anno.id}"]`);
    if (!mark) return;
    positions.push({ id: anno.id, top: mark.offsetTop, mark });

    if (anno.commentBody && layer && article) {
      const gutter = document.createElement('button');
      gutter.type = 'button';
      gutter.className = 'guides-anno-gutter';
      gutter.dataset.annoId = String(anno.id);
      gutter.setAttribute('aria-label', 'Open comment');
      gutter.innerHTML = '<i class="bi bi-chat-left-text" aria-hidden="true"></i>';
      const top = mark.offsetTop;
      gutter.style.top = `${top}px`;
      gutter.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onGutterClick?.(anno.id);
      });
      layer.appendChild(gutter);
    }
  });

  return positions;
}

export function findAnnotationMark(root, annotationId) {
  if (!root || annotationId == null) return null;
  return root.querySelector(`mark.guides-anno[data-anno-id="${annotationId}"]`);
}

export function formatAnnotationDateTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return '';
  }
}

export function authorDisplayName(author) {
  if (!author) return 'You';
  const name = [author.firstName, author.lastName].filter(Boolean).join(' ').trim();
  return name || 'You';
}

/** Unwrap in-article find highlights. */
export function clearFindHighlights(root) {
  if (!root) return;
  const marks = Array.from(root.querySelectorAll('mark.guides-find-hit'));
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
  });
  root.normalize();
}

/**
 * Highlight all case-insensitive matches of query in root.
 * Returns the list of mark elements (empty if no query / no hits).
 */
export function applyFindHighlights(root, query) {
  clearFindHighlights(root);
  const q = String(query || '').trim();
  if (!root || !q) return [];

  const qLower = q.toLowerCase();
  const marks = [];
  // Snapshot nodes first — wrapping mutates the tree
  const nodes = getContentTextNodes(root);

  nodes.forEach((textNode) => {
    const text = textNode.nodeValue || '';
    if (!text) return;
    const lower = text.toLowerCase();
    let from = 0;
    const segments = []; // { start, end, hit }

    while (from < text.length) {
      const idx = lower.indexOf(qLower, from);
      if (idx < 0) {
        if (from < text.length) segments.push({ start: from, end: text.length, hit: false });
        break;
      }
      if (idx > from) segments.push({ start: from, end: idx, hit: false });
      segments.push({ start: idx, end: idx + q.length, hit: true });
      from = idx + q.length;
    }

    if (!segments.some((s) => s.hit)) return;

    const parent = textNode.parentNode;
    if (!parent) return;
    const frag = document.createDocumentFragment();
    segments.forEach((seg) => {
      const slice = text.slice(seg.start, seg.end);
      if (!seg.hit) {
        frag.appendChild(document.createTextNode(slice));
        return;
      }
      const mark = document.createElement('mark');
      mark.className = 'guides-find-hit';
      mark.textContent = slice;
      frag.appendChild(mark);
      marks.push(mark);
    });
    parent.replaceChild(frag, textNode);
  });

  return marks;
}

/** Set which find hit is active and scroll it into view. */
export function focusFindHit(marks, index) {
  if (!marks?.length) return -1;
  const i = ((index % marks.length) + marks.length) % marks.length;
  marks.forEach((m, idx) => {
    m.classList.toggle('is-current', idx === i);
  });
  const current = marks[i];
  current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return i;
}
